import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { UserRound, Save, MessageCircle, CheckCircle2, Info, Send, Building2, ArrowRight } from "lucide-react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "../../phone-dark.css";
import { api, apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { CompanyForm } from "../../components/CompanyForm";
import { BackToCampaigns } from "../../components/campaign/BackToCampaigns";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const toE164 = (raw) => {
  const d = (raw || "").replace(/\D/g, "");
  if (!d) return "";
  if (d.length === 10) return "+52" + d;
  return "+" + d;
};

export default function CompanyProfile() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name && user.name !== "Invitado" ? user.name : "");
  const [whatsapp, setWhatsapp] = useState(toE164(user?.whatsapp || user?.phone || ""));
  const [saving, setSaving] = useState(false);

  const waValid = whatsapp && isValidPhoneNumber(whatsapp);

  const save = async (e) => {
    e.preventDefault();
    if (whatsapp && !waValid) { toast.error("El número de WhatsApp no es válido. Verifica el país y los dígitos."); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/profile", { name: name || "", whatsapp: whatsapp || "" });
      setUser(data);
      toast.success("Perfil guardado");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const testWhatsApp = () => {
    if (!waValid) { toast.error("Primero escribe un WhatsApp válido."); return; }
    const digits = whatsapp.replace(/\D/g, "");
    window.open(`https://wa.me/${digits}?text=${encodeURIComponent("Hola 👋 Esta es una prueba de mi WhatsApp configurado en MULTICONTACTOS. ¡Funciona!")}`, "_blank");
  };

  return (
    <div data-testid="profile-page" className="max-w-2xl">
      <BackToCampaigns />
      <div className="flex items-center gap-3 mb-2">
        <UserRound className="text-purple-500" size={26} />
        <h1 className="font-display font-black tracking-tight text-3xl">Perfil del Líder</h1>
      </div>
      <p className="text-zinc-400 mb-8">Tus datos como líder y la empresa con la que vas a trabajar.</p>

      <form onSubmit={save} className="space-y-6 rounded-2xl border border-white/10 bg-[#121212] p-7">
        <div>
          <Label className="text-zinc-300">Nombre del Líder <span className="text-zinc-500 text-xs">(opcional)</span></Label>
          <Input
            data-testid="leader-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ej. Ricardo Zermeño"
            className="mt-2 bg-black border-white/15 rounded-xl py-6 focus-visible:ring-purple-500"
          />
        </div>

        {/* WhatsApp internacional */}
        <div className="rounded-2xl border border-green-500/25 bg-green-500/5 p-5">
          <div className="flex items-center gap-2 mb-1">
            <MessageCircle className="text-green-400" size={20} />
            <Label className="text-zinc-100 text-base font-semibold">Tu WhatsApp para recibir prospectos</Label>
          </div>
          <p className="text-sm text-zinc-400 mb-4">Es <strong className="text-zinc-200">tu número personal</strong>. Cada prospecto calificado por la IA llegará aquí.</p>
          <div className="rounded-xl bg-black/40 border border-white/10 p-4 mb-4">
            <div className="flex items-center gap-2 text-zinc-300 text-sm font-semibold mb-2"><Info size={15} className="text-purple-400" /> ¿Cómo se hace?</div>
            <ol className="text-xs text-zinc-400 space-y-1.5 list-decimal list-inside">
              <li>Elige tu <strong className="text-zinc-300">país</strong> en la bandera (el prefijo, ej. +52, +57, +1, se agrega solo).</li>
              <li>Escribe tu número como lo marcarías localmente (sin el prefijo).</li>
              <li>Guarda. La plataforma crea tu enlace <span className="font-mono text-zinc-300">wa.me</span> individual.</li>
            </ol>
            <p className="text-[11px] text-zinc-500 mt-2.5">💡 Tip: primero elige tu país en la bandera; no pegues el prefijo (ej. +57) al inicio del número.</p>
          </div>
          <div className="phone-dark">
            <PhoneInput international countryCallingCodeEditable={false} defaultCountry="MX" value={whatsapp} onChange={(v) => setWhatsapp(v || "")} placeholder="Escribe tu número" />
          </div>
          <div className="flex items-center justify-between flex-wrap gap-2 mt-2">
            <p className={`text-xs flex items-center gap-1.5 ${whatsapp ? (waValid ? "text-green-400" : "text-red-400") : "text-zinc-500"}`}>
              {whatsapp ? (waValid ? <><CheckCircle2 size={13} /> Número válido</> : "Número incompleto o inválido para el país seleccionado") : "Selecciona tu país y escribe tu número con WhatsApp activo."}
            </p>
            <Button type="button" data-testid="test-whatsapp-button" onClick={testWhatsApp} disabled={!waValid} variant="outline" className="border-green-500/40 text-green-400 hover:bg-green-500/10 rounded-full px-4 h-9 text-xs disabled:opacity-40">
              <Send size={14} className="mr-1.5" /> Probar mi WhatsApp
            </Button>
          </div>
        </div>

        <Button data-testid="save-profile-button" type="submit" disabled={saving} className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-xl px-6 py-6">
          <Save size={18} className="mr-2" /> {saving ? "Guardando..." : "Guardar perfil"}
        </Button>
      </form>

      {/* Datos de la empresa de multinivel */}
      <div className="mt-8 rounded-2xl border border-white/10 bg-[#121212] p-7">
        <div className="flex items-center gap-2 mb-1">
          <Building2 className="text-purple-400" size={22} />
          <h2 className="font-display font-bold text-xl">Datos de la empresa de multinivel</h2>
        </div>
        <p className="text-sm text-zinc-400 mb-5">Agrega la empresa con la que harás campañas. La IA usará estos datos para crear tus anuncios, imágenes y videos.</p>
        <CompanyForm onSaved={() => toast.success("Empresa lista. Adminístralas en el menú 'Empresas'.")} />
        <Link to="/dashboard/empresas" className="mt-5 inline-flex items-center gap-1.5 text-sm text-yellow-impact hover:text-yellow-300 font-semibold">
          Ver y administrar todas mis empresas <ArrowRight size={15} />
        </Link>
      </div>
    </div>
  );
}
